import {
  CloudWatchLogsClient,
  GetLogEventsCommand,
  FilterLogEventsCommand,
  type OutputLogEvent,
  type FilteredLogEvent,
} from '@aws-sdk/client-cloudwatch-logs';
import { DEPLOY_ERROR_BOX_FILTER_PATTERN, stripAnsi } from '../../../lib/deploy-failure';
import { getConfigValue } from './configService';
import type { PortalConfig } from './configService';
import { authService } from './authService';
import { awsCredentialsService } from './awsCredentialsService';
import type { DeploymentRecord } from './deploymentService';

// One ANSI stripper for the portal and the deploy pipeline, so both read a log line the same way.
export { stripAnsi };

const DEFAULT_LOG_GROUP = '/ecs/numa-portal-deploy';

function getRegion(): string {
  return getConfigValue('AWS_REGION') || 'us-east-1';
}

// The page polls, so the client is kept: a new one for each call would fetch
// new AWS credentials for each call. A different person signed in gets a new one.
let cachedClient: { client: CloudWatchLogsClient; owner: string } | null = null;

function getLogsClient(): CloudWatchLogsClient {
  const owner = `${authService.getCurrentUser()?.email || ''}|${getRegion()}`;
  if (!cachedClient || cachedClient.owner !== owner) {
    cachedClient = {
      client: awsCredentialsService.withRevokedRetry(
        new CloudWatchLogsClient({ region: getRegion(), credentials: awsCredentialsService.credentials() })
      ),
      owner,
    };
  }
  return cachedClient.client;
}

/** CloudWatch refused the call because the account is over its request rate. The caller must slow down. */
export function isThrottlingError(error: unknown): boolean {
  const err = error as { name?: string; message?: string; $metadata?: { httpStatusCode?: number } };
  return (
    err?.name === 'ThrottlingException' ||
    err?.name === 'TooManyRequestsException' ||
    err?.$metadata?.httpStatusCode === 429 ||
    /rate exceeded|throttl/i.test(err?.message || '')
  );
}

/** The log stream does not exist: the task has not written a line yet, or the log is past its 30 days. */
export function isMissingStreamError(error: unknown): boolean {
  return (error as { name?: string })?.name === 'ResourceNotFoundException';
}

export interface LogTarget {
  group: string;
  stream?: string;
}

/** The last segment of a task ARN, which is also the tail of its log stream name. */
export function taskIdFromArn(taskArn?: string): string | undefined {
  if (!taskArn) return undefined;
  return taskArn.split('/').pop() || taskArn;
}

export function logStreamForTask(taskArn?: string): string | undefined {
  const taskId = taskIdFromArn(taskArn);
  return taskId ? `ecs/deployer/${taskId}` : undefined;
}

/**
 * Where a run's log lives. Each deploy task writes one stream, named after the
 * task. Pass `taskArn` to read an earlier attempt's stream in place of the
 * current one.
 */
export function resolveDeploymentLogTarget(dep: DeploymentRecord, taskArn?: string): LogTarget {
  const group = dep.logsGroup || getConfigValue('LOG_GROUP_NAME' as keyof PortalConfig) || DEFAULT_LOG_GROUP;
  const stream = taskArn ? logStreamForTask(taskArn) : dep.logsStream || logStreamForTask(dep.ecsTaskArn);
  return { group, stream };
}

export interface FilteredPage {
  events: FilteredLogEvent[];
  nextToken?: string;
}

/**
 * One page of a filtered read. CloudWatch searches a bounded slice for each
 * call, so a page can come back empty and still carry a token for the next.
 */
export async function filterLogPage(params: {
  group: string;
  streams: string[];
  pattern?: string;
  startTime?: number;
  endTime?: number;
  nextToken?: string;
}): Promise<FilteredPage> {
  const res = await getLogsClient().send(
    new FilterLogEventsCommand({
      logGroupName: params.group,
      logStreamNames: params.streams,
      filterPattern: params.pattern,
      startTime: params.startTime,
      endTime: params.endTime,
      nextToken: params.nextToken,
    })
  );
  return { events: res.events ?? [], nextToken: res.nextToken };
}

/** CloudWatch takes at most 100 stream names in one filtered read. */
export const MAX_STREAMS_PER_READ = 100;

/**
 * The error lines of up to 100 runs in one read, split by stream. A stream with
 * no error line is absent from the result.
 *
 * It reads with the deploy pipeline's own filter, which returns each whole
 * Terraform error box, so the portal and the pipeline sort a failure the same
 * way. Measured on 70 failed runs: 934 lines, against about 1.7 million for the
 * whole logs.
 */
export async function readErrorLines(params: {
  group: string;
  streams: string[];
  startTime: number;
  endTime: number;
  maxPages?: number;
}): Promise<Map<string, string[]>> {
  const byStream = new Map<string, string[]>();
  let token: string | undefined;
  const maxPages = params.maxPages ?? 40;
  for (let page = 0; page < maxPages; page++) {
    const res = await filterLogPage({
      group: params.group,
      streams: params.streams.slice(0, MAX_STREAMS_PER_READ),
      pattern: DEPLOY_ERROR_BOX_FILTER_PATTERN,
      startTime: params.startTime,
      endTime: params.endTime,
      nextToken: token,
    });
    for (const event of res.events) {
      if (!event.logStreamName) continue;
      const lines = byStream.get(event.logStreamName) || [];
      lines.push(event.message || '');
      byStream.set(event.logStreamName, lines);
    }
    token = res.nextToken;
    if (!token) break;
  }
  return byStream;
}

export interface LogChunk {
  events: OutputLogEvent[];
  /** Pass to `loadLogEarlier` for the lines before this chunk. */
  backwardToken?: string;
  /** Pass to `tailLogStream` to carry on from the last line of this chunk. */
  forwardToken?: string;
  /** No line is older than this chunk. */
  reachedStart: boolean;
}

/** How many lines the log page loads at a time. */
export const LOG_CHUNK_LINES = 3000;

async function readBackward(
  group: string,
  stream: string,
  token: string | undefined,
  limit: number
): Promise<LogChunk> {
  const cw = getLogsClient();
  let events: OutputLogEvent[] = [];
  let backwardToken = token;
  let forwardToken: string | undefined;
  let reachedStart = false;
  // CloudWatch can answer with an empty page and a new token. Follow it a few times before giving up.
  for (let attempt = 0; attempt < 6 && events.length < limit; attempt++) {
    const res = await cw.send(
      new GetLogEventsCommand({
        logGroupName: group,
        logStreamName: stream,
        startFromHead: false,
        limit: Math.min(10000, limit - events.length),
        nextToken: backwardToken,
      })
    );
    if (attempt === 0) forwardToken = res.nextForwardToken;
    const batch = res.events ?? [];
    events = [...batch, ...events];
    // The same token back means there is nothing older.
    if (!res.nextBackwardToken || res.nextBackwardToken === backwardToken) {
      reachedStart = true;
      break;
    }
    backwardToken = res.nextBackwardToken;
  }
  return { events, backwardToken, forwardToken, reachedStart };
}

/**
 * The END of a log: its last `limit` lines. A deploy log is about 25,000 lines
 * and the part a person wants (the apply and any error) is at the end.
 */
export function loadLogTail(group: string, stream: string, limit = LOG_CHUNK_LINES): Promise<LogChunk> {
  return readBackward(group, stream, undefined, limit);
}

/** The `limit` lines before a chunk already on screen. */
export function loadLogEarlier(
  group: string,
  stream: string,
  backwardToken: string,
  limit = LOG_CHUNK_LINES
): Promise<LogChunk> {
  return readBackward(group, stream, backwardToken, limit);
}

export interface TailOptions {
  group: string;
  stream: string;
  // If true, start at the end (tail only new lines). If false, start from head.
  startAtEnd?: boolean;
  // Poll interval in ms
  intervalMs?: number;
  // Initial last seen timestamp (ms) to avoid overlap with a prior backfill
  initialLastTs?: number;
  /**
   * Carry on from the end of a chunk `loadLogTail` returned. With it the tail
   * starts exactly after the chunk's last line: nothing repeats and nothing is
   * skipped.
   */
  initialForwardToken?: string;
  // Callback when new events arrive
  onEvents: (events: OutputLogEvent[]) => void;
  onError?: (err: unknown) => void;
  // Optional: report lastTs updates to caller
  onLastTsUpdate?: (ts: number) => void;
}

export function tailLogStream(opts: TailOptions) {
  const cw = getLogsClient();
  const interval = Math.max(500, opts.intervalMs ?? 1500);
  let nextToken: string | undefined = opts.initialForwardToken;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let started = Boolean(opts.initialForwardToken);
  let lastTs = opts.initialLastTs && Number.isFinite(opts.initialLastTs) ? (opts.initialLastTs as number) : 0;
  // A token from `loadLogTail` already sits after the last line shown, so no time filter is needed on top of it.
  const filterByTime = !opts.initialForwardToken;
  let idleTicks = 0;
  let delay = interval;
  const seen = new Set<string>();
  const seenOrder: string[] = [];
  const seenCap = 20000;

  function keyFor(e: Pick<OutputLogEvent, 'timestamp' | 'ingestionTime' | 'message'>): string {
    const ts = e.timestamp ?? 0;
    const ing = e.ingestionTime ?? 0;
    const msg = e.message ?? '';
    return `${ts}|${ing}|${msg.length}:${msg.slice(0, 200)}`;
  }

  function dedup(events: OutputLogEvent[]): OutputLogEvent[] {
    const out: OutputLogEvent[] = [];
    for (const e of events) {
      const k = keyFor(e);
      if (seen.has(k)) continue;
      out.push(e);
      seen.add(k);
      seenOrder.push(k);
      if (seenOrder.length > seenCap) {
        const old = seenOrder.shift()!;
        seen.delete(old);
      }
    }
    return out;
  }

  function deliver(events: OutputLogEvent[]) {
    if (!events.length) return;
    opts.onEvents(events);
    const ts = events[events.length - 1]?.timestamp;
    if (typeof ts === 'number') {
      lastTs = Math.max(lastTs, ts);
      opts.onLastTsUpdate?.(lastTs);
    }
  }

  async function tick() {
    if (stopped) return;
    try {
      if (!started) {
        // Prime nextToken by calling GetLogEvents; when startFromHead=false this positions us at the tail
        const prime = await cw.send(
          new GetLogEventsCommand({
            logGroupName: opts.group,
            logStreamName: opts.stream,
            startFromHead: opts.startAtEnd ? false : true,
            limit: 1000,
          })
        );
        nextToken = prime.nextForwardToken;
        if (!opts.startAtEnd && (prime.events?.length ?? 0) > 0) deliver(dedup(prime.events!));
        started = true;
      } else {
        const res = await cw.send(
          new GetLogEventsCommand({
            logGroupName: opts.group,
            logStreamName: opts.stream,
            nextToken,
            startFromHead: false,
            limit: 1000,
          })
        );
        const newToken = res.nextForwardToken;
        if (newToken && newToken !== nextToken) {
          const events = res.events ?? [];
          const filtered = filterByTime && lastTs ? events.filter((e) => (e.timestamp ?? 0) > lastTs) : events;
          deliver(dedup(filtered));
          nextToken = newToken;
          idleTicks = 0;
        } else {
          idleTicks += 1;
          // Guard refresh: after several idle ticks, search for late-arriving events near the last timestamp
          if (idleTicks >= 6) {
            try {
              const windowStart = lastTs ? lastTs + 1 : Date.now() - 60_000;
              const filter = await filterLogPage({
                group: opts.group,
                streams: [opts.stream],
                startTime: windowStart,
              });
              const events: OutputLogEvent[] = filter.events.map((e: FilteredLogEvent) => ({
                message: e.message,
                timestamp: e.timestamp,
              }));
              deliver(dedup(events.filter((e) => (e.timestamp ?? 0) > lastTs)));
              // Re-prime token to the current tail to avoid being stuck
              const rePrime = await cw.send(
                new GetLogEventsCommand({
                  logGroupName: opts.group,
                  logStreamName: opts.stream,
                  startFromHead: false,
                  limit: 1,
                })
              );
              if (rePrime.nextForwardToken) nextToken = rePrime.nextForwardToken;
            } catch (err) {
              console.debug('[logs] resync error', err);
            } finally {
              idleTicks = 0;
            }
          }
        }
      }
      delay = interval;
    } catch (e: unknown) {
      if (isMissingStreamError(e)) {
        // The task has not written a line yet. Keep trying quietly.
      } else if (isThrottlingError(e)) {
        // The whole team shares one CloudWatch request quota. Slow down, do not report.
        delay = Math.min(delay * 2, 30_000);
      } else if (opts.onError) {
        opts.onError(e);
      }
    } finally {
      if (!stopped) timer = setTimeout(tick, delay);
    }
  }

  function start() {
    if (timer) return;
    stopped = false;
    timer = setTimeout(tick, 0);
  }
  function stop() {
    stopped = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function getLastTs() {
    return lastTs;
  }

  return { start, stop, getLastTs };
}
